import { useCallback, useEffect, useState } from 'react';
import type { Fact, FactSource, FactStatus } from '@stonelink/shared';
import { api } from '../api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  PageHeader,
  Textarea,
  formatDate,
} from '../ui';

const SOURCE_LABEL: Record<FactSource, string> = {
  training_chat: 'Trained',
  manual: 'Added manually',
  gap_resolution: 'From a gap',
};

/** The knowledge base: everything the assistant is allowed to say. */
export function NotebookPage() {
  const [facts, setFacts] = useState<Fact[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<FactStatus>('active');
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setFacts(await api.listFacts({ q: search || undefined, status }));
      setError(null);
    } catch {
      setError('Could not load the notebook.');
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    // Debounced so typing in the search box does not hammer the API.
    const timer = setTimeout(() => void load(), 200);
    return () => clearTimeout(timer);
  }, [load]);

  const add = async () => {
    const content = draft.trim();
    if (content.length < 3) return;
    try {
      await api.createFact(content);
      setDraft('');
      await load();
    } catch {
      setError('Could not save that fact.');
    }
  };

  return (
    <div>
      <PageHeader
        title="Notebook"
        description="Everything your assistant knows. It will never tell a customer anything that is not here."
      />

      <div className="space-y-5">
        <Card
          title="Add a fact"
          description="Write it the way you would want a customer to hear it."
        >
          <div className="flex flex-col gap-2 sm:flex-row">
            <Textarea
              rows={2}
              value={draft}
              placeholder="Returns are accepted within 30 days of purchase with a receipt."
              aria-label="New fact"
              onChange={(event) => setDraft(event.target.value)}
            />
            <Button onClick={() => void add()} disabled={draft.trim().length < 3}>
              Add
            </Button>
          </div>
        </Card>

        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-56 flex-1">
            <Input
              type="search"
              value={search}
              placeholder="Search the notebook…"
              aria-label="Search facts"
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="flex gap-1 rounded-xl border border-line bg-white/5 p-1">
            {(['active', 'archived'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatus(value)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize transition ${
                  status === value ? 'bg-brand text-[#15102a]' : 'text-muted'
                }`}
              >
                {value}
              </button>
            ))}
          </div>
        </div>

        {error && <ErrorNote>{error}</ErrorNote>}

        {loading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : facts.length === 0 ? (
          <EmptyState
            title={status === 'active' ? 'Nothing here yet' : 'Nothing archived'}
            hint={
              status === 'active'
                ? 'Head to Train and tell your assistant about the business.'
                : undefined
            }
          />
        ) : (
          <ul className="space-y-2">
            {facts.map((fact) => (
              <FactRow key={fact.id} fact={fact} onChanged={() => void load()} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function FactRow({ fact, onChanged }: { fact: Fact; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(fact.content);
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="glass-flat p-4">
      {editing ? (
        <div className="space-y-2">
          <Textarea
            rows={3}
            value={content}
            aria-label="Fact content"
            onChange={(event) => setContent(event.target.value)}
          />
          <div className="flex gap-2">
            <Button
              disabled={busy || content.trim().length < 3}
              onClick={() =>
                void run(async () => {
                  await api.updateFact(fact.id, { content: content.trim() });
                  setEditing(false);
                })
              }
            >
              Save
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setContent(fact.content);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm leading-relaxed">{fact.content}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted">
            <Badge tone={fact.source === 'gap_resolution' ? 'brand' : 'neutral'}>
              {SOURCE_LABEL[fact.source]}
            </Badge>
            <span>Updated {formatDate(fact.updatedAt)}</span>
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="font-medium text-brand hover:underline"
            >
              Edit
            </button>
            {fact.status === 'active' ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(() => api.updateFact(fact.id, { status: 'archived' }))}
                className="font-medium hover:text-red-300 hover:underline"
              >
                Archive
              </button>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(() => api.updateFact(fact.id, { status: 'active' }))}
                className="font-medium text-brand hover:underline"
              >
                Restore
              </button>
            )}
          </div>
        </>
      )}
    </li>
  );
}
